/**
 * Content feed sort.
 *
 * Topic chips in sections/content-feed.liquid are links, so filtering needs
 * nothing here. The sort control is a GET form; this submits it when the
 * select changes, so the visitor does not need a separate button. With
 * JavaScript off, the noscript submit button in the form does the same job.
 */

if (!customElements.get('content-feed')) {
  class ContentFeed extends HTMLElement {
    abortController = undefined;

    connectedCallback() {
      this.abortController = new AbortController();
      const {signal} = this.abortController;

      for (const button of this.querySelectorAll('[data-content-feed-drawer-open]')) {
        button.addEventListener('click', this.onDrawerOpen.bind(this), {signal});
      }

      this.querySelector('[data-content-feed-show-topics]')?.addEventListener('click', this.onShowTopics.bind(this), {signal});

      for (const input of this.querySelectorAll('[data-content-feed-topic], [data-content-feed-sort-radio]')) {
        input.addEventListener('change', this.rebuildApply.bind(this), {signal});
      }

      this.preserveQuery();
    }

    /**
     * Feed links are rendered with only ?view=<suffix> and the sort. Every
     * other parameter in the address bar is added here, without overriding one
     * the link already sets.
     */
    preserveQuery() {
      const current = new URLSearchParams(window.location.search);
      current.delete('page');

      const base = this.dataset.feedBase;

      if (!base || [...current.keys()].length === 0) {
        return;
      }

      for (const link of this.querySelectorAll('a[href]')) {
        const url = new URL(link.href, window.location.origin);

        if (url.origin !== window.location.origin) {
          continue;
        }

        if (url.pathname !== base && !url.pathname.startsWith(`${base}/tagged/`)) {
          continue;
        }

        for (const [key, value] of current) {
          if (key !== 'sort' && !url.searchParams.has(key)) {
            url.searchParams.set(key, value);
          }
        }

        link.href = `${url.pathname}${url.search}${url.hash}`;
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    /**
     * @param {Event} event
     */
    onDrawerOpen(event) {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      const dialog = this.querySelector('[data-content-feed-drawer]');
      if (!(dialog instanceof HTMLDialogElement)) {
        return;
      }

      const sortGroup = dialog.querySelector('[data-content-feed-sort-group]');
      const topicGroup = dialog.querySelector('[data-content-feed-topic-group]');
      const openSort = (button.dataset.contentFeedDrawerOpen === 'sort');

      if (sortGroup instanceof HTMLDetailsElement) {
        sortGroup.open = openSort;
      }

      if (topicGroup instanceof HTMLDetailsElement) {
        topicGroup.open = !openSort;
      }

      if (!dialog.open) {
        dialog.showModal();
      }
    }

    onShowTopics() {
      for (const extra of this.querySelectorAll('[data-content-feed-topic-extra]')) {
        extra.hidden = false;
      }

      this.querySelector('[data-content-feed-show-topics]')?.setAttribute('hidden', '');
    }

    rebuildApply() {
      const apply = this.querySelector('[data-content-feed-apply]');
      if (!(apply instanceof HTMLAnchorElement)) {
        return;
      }

      const kept = apply.dataset.kept ? apply.dataset.kept.split('+').filter(Boolean) : [];
      const topics = [...this.querySelectorAll('[data-content-feed-topic]:checked')].map((input) => input.value);
      const sortInput = this.querySelector('[data-content-feed-sort-radio]:checked');
      const sort = sortInput instanceof HTMLInputElement ? sortInput.value : 'newest';
      const tags = [...kept, ...topics];
      let url = apply.dataset.base || apply.href;

      if (tags.length > 0) {
        url += `/tagged/${tags.join('+')}`;
      }

      const params = new URLSearchParams(window.location.search);
      params.delete('page');

      if (sort && sort !== 'newest') {
        params.set('sort', sort);
      } else {
        params.delete('sort');
      }

      const query = params.toString();

      apply.href = query ? `${url}?${query}` : url;
    }
  }

  customElements.define('content-feed', ContentFeed);
}

if (!customElements.get('content-feed-sort')) {
  class ContentFeedSort extends HTMLElement {
    abortController = undefined;

    connectedCallback() {
      this.abortController = new AbortController();
      const select = this.querySelector('[data-content-feed-sort]');

      select?.addEventListener('change', this.onSortChange.bind(this), {
        signal: this.abortController.signal,
      });

      this.preserveQuery(select?.form);
    }

    /**
     * A GET submit replaces the whole query string with the form's fields, so
     * every other address-bar parameter is mirrored into a hidden field.
     * @param {HTMLFormElement | null | undefined} form
     */
    preserveQuery(form) {
      if (!form) {
        return;
      }

      for (const [key, value] of new URLSearchParams(window.location.search)) {
        if (key === 'sort' || key === 'page' || form.elements.namedItem(key)) {
          continue;
        }

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.append(input);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    /**
     * @param {Event} event
     */
    onSortChange(event) {
      const select = event.currentTarget;
      if (!(select instanceof HTMLSelectElement)) {
        return;
      }

      select.form?.requestSubmit();
    }
  }

  customElements.define('content-feed-sort', ContentFeedSort);
}
